import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { Button } from "react-md/lib/Buttons";
import { MDialog, MGrid, MSelect, MSpinner, TextBlock } from "components/Shared";
import { CardText, DialogContainer, TextField } from "react-md";
import { addCaseFormRedux, getAllCaseFormsRedux, getAvailCaseFormsListRedux, deleteCaseForm, manageFormsRedux, updateCaseForm } from "requests/homeowner/ho";
import { reAddCaseForms as reAddCaseFormsRequest } from "requests/homeowner/caseforms";
import responseHandler from "responseHandler";
import { getCaseForms } from "actions/homeowner/caseforms";
import { usePhoenixForms } from "components/PhoenixFormsV4/index.tsx";
import { AvailFormDto, CaseFormAddRequest, CaseFormDeleteRequest, CaseFormDto, CaseFormUpdateRequest } from "interfaces/Mueller.Business.Models";

interface ManageFormsDialogProps {
  controlNumber: number;
  availCaseFormsList: AvailFormDto[];
  allCaseFormsList: CaseFormDto[];
  getAllCaseForms: (controlNumber: number) => void;
  getAvailCaseFormsList: (controlNumber: number) => void;
  addCaseForm: (controlNumber: number, payload: CaseFormAddRequest, callback?: () => void) => void;
  deleteCaseForm: (controlNumber: number, payload: CaseFormDeleteRequest, callback?: () => void) => void;
  manageForms: boolean;
  cancelManageForms: (payload?: boolean) => void;
  updateCaseForm: (controlNumber: number, payload: CaseFormUpdateRequest) => void;
  fetchingAllCaseForms: boolean;
  makeToast?: (text?: string, type?: string) => void;
  reloadCase?: () => void;
  getCaseForms?: (controlNumber?: number) => void;
}

const ADD = "ADD";
const REMOVE = "REMOVE";
const UPDATE = "UPDATE";
const READD = "READD";

const ManageFormsDialog = ({
  availCaseFormsList,
  allCaseFormsList,
  getAllCaseForms,
  deleteCaseForm,
  addCaseForm,
  getAvailCaseFormsList,
  makeToast,
  controlNumber,
  manageForms,
  cancelManageForms,
  fetchingAllCaseForms,
  reloadCase,
  getCaseForms,
}: ManageFormsDialogProps) => {

  const [selectedCaseForm, setSelectedCaseForm] = useState(null);
  const [selectedGridIndexes, setSelectedGridIndexes] = useState([]);
  const [showRemoveFormDialog, setShowRemoveFormWarning] = useState(false);
  const [isReaddingCaseForm, setIsReaddingCaseForm] = useState(false);
  // useRef to call all case forms endpoint after adding/removing a form to update MGrid of current forms since refresh has not been implemented yet
  // location.reload() is disruptive to workflow since it causes whole site to reload aka QA agent must "Start QA" again
  const recallAllCaseForms = useRef(null);
  const formTitle = useRef(null);
  const mounted = useRef(true);

  const { reload } = usePhoenixForms();

  useEffect(() => {
    if (controlNumber) {
      getAllCaseForms(controlNumber);
      getAvailCaseFormsList(controlNumber);
    }
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    getAllCaseForms(controlNumber);
    if (recallAllCaseForms.current) {
      getAllCaseForms(controlNumber);
      recallAllCaseForms.current = null;
    }
    return () => { };
  }, [controlNumber, recallAllCaseForms.current]);

  const successCallback = (action, formName?) => {
    recallAllCaseForms.current = true;
    getCaseForms(controlNumber);
    getAvailCaseFormsList(controlNumber);
    switch (action) {
      case ADD:
        makeToast(`${formName} added to this case.`, "success");
        reload();
        if (reloadCase)
          reloadCase();        
        return;
      case UPDATE:
        makeToast(`${formName} updated.`, "success");
        reload();
        if (reloadCase)
          reloadCase();
        return;
      case REMOVE:
        makeToast(`${formName} removed from this case.`, "success");
        reload();
        if (reloadCase)
          reloadCase();
        return;
      case READD:
        makeToast("Forms re-added successfully.", "success");
        reload();
        if (reloadCase)
          reloadCase();
        return;
      default:
        return;
    }
  };

  const submitCaseFormAction = (a) => {
    if (a === ADD && selectedCaseForm) {
      if (allCaseFormsList?.some(f => f.availForm.id === selectedCaseForm.id && f.availForm.instanceCountMaximum === 1 && !f.isDeleted)) return makeToast(`${selectedCaseForm.name} is already added to this case.`, "error");
      addCaseForm(controlNumber, { "availFormId": selectedCaseForm.id, "controlIdNumber": controlNumber, "title": formTitle.current }, () => successCallback(a, selectedCaseForm.name));
      
      setSelectedCaseForm(null);
      formTitle.current = null;
    } else if (a === ADD && !selectedCaseForm) {
      return makeToast("Please select a form from the dropdown to add to this case.", "error");
    } else if (a === UPDATE && selectedGridIndexes.length === 1) {
      if (!formTitle.current) return makeToast("Please add a title to update this form.", "error");
      let filteredGridData = allCaseFormsList.filter(f => !f.isDeleted);
      let idx = selectedGridIndexes[0];
      updateCaseForm(
        controlNumber,
        { 
          "id": filteredGridData[idx].id,
          "controlIdNumber": controlNumber,
          "title": formTitle.current
        },
        () => {
          successCallback(a, filteredGridData[idx].availForm.displayName);
        },
        mounted
      );
      setSelectedGridIndexes([]);
      formTitle.current = null;
      cancelManageForms(false);
    } else if (a === UPDATE && (!selectedGridIndexes.length || selectedGridIndexes.length > 1)) {
      return makeToast("Please select ONE form from the grid to update.", "error");
    } else if (a === REMOVE && selectedGridIndexes.length > 0) {
      setShowRemoveFormWarning(true); // show warning and ask if user wants to continue
      cancelManageForms(false); // hide manage forms dialog while user decides whether to remove forms or not
    } else if (a === REMOVE && !selectedGridIndexes.length) {
      return makeToast("Please select a form from the grid to remove from this case.", "error");
    }
  };

  const readdCaseFormAsync = (gridIndex: number) => {
    return new Promise((resolve, reject) => {
      reAddCaseFormsRequest(controlNumber, allCaseFormsList[gridIndex].id, mounted, resolve, () => {
        makeToast("There was an error re-adding this form to the case.", "error");
        reject();
      });
    });
  };

  const readdCaseForms = async () => {
    setIsReaddingCaseForm(true);
    await Promise.all(selectedGridIndexes.map(readdCaseFormAsync));
  };

  const isUpdatingAndRemovingDisabled = !selectedGridIndexes || !selectedGridIndexes.length || selectedGridIndexes.some(i => allCaseFormsList[i].isDeleted === true);
  const isReAddingDisabled = !selectedGridIndexes || !selectedGridIndexes.length || !selectedGridIndexes.every(i => allCaseFormsList[i].isDeleted === true);

  const getManageFormDialogActions = () => {

    return [
      {
        primary: true,
        children: "Add",
        raised: true,
        disabled: !selectedCaseForm,
        onClick: () => submitCaseFormAction(ADD)
      },
      {
        primary: true,
        children: "Update",
        raised: true,
        disabled: isUpdatingAndRemovingDisabled,
        onClick: () => submitCaseFormAction(UPDATE)
      },
      {
        primary: true,
        children: "Remove",
        raised: true,
        disabled: isUpdatingAndRemovingDisabled,
        onClick: () => submitCaseFormAction(REMOVE)
      },
      {
        primary: true,
        children: "Re-Add",
        raised: true,
        disabled: isReAddingDisabled, //only allow them to re-add if all selected forms are deleted
        onClick: () => {
          readdCaseForms().then(() => {
            setIsReaddingCaseForm(false);
            successCallback(READD);
          });
        }
      },
      {
        secondary: true,
        children: "Close",
        raised: true,
        onClick: () => {
          setSelectedCaseForm(null);
          setSelectedGridIndexes([]);
          cancelManageForms(false);
          formTitle.current = null;
        }
      }
    ];
  };

  const submitRemoveCaseForm = () => {
    selectedGridIndexes.map(i => allCaseFormsList[i]).forEach(f => {
      if (!f.isDeleted && f.currentUserCanDelete) {
        deleteCaseForm(controlNumber, { "id": f.id, "controlIdNumber": controlNumber }, () => successCallback(REMOVE, f.availForm.displayName));
        setSelectedGridIndexes([]);
        setShowRemoveFormWarning(false);
      } else makeToast(`There was an error removing ${f.availForm.name} from this case.`, "error");
    });
  };

  const removeFormDialogActions = [
    <Button
      className="m-t-10"
      key="continue"
      raised
      primary
      onClick={submitRemoveCaseForm} // submit remove case forms 
    >
      Continue
    </Button>,
    <Button
      className="m-t-10"
      key="cancel"
      raised
      primary
      onClick={() => {
        setShowRemoveFormWarning(false); // hide warning to remove forms from cases
        cancelManageForms(true); // show manage forms dialog again
      }}
    >
      Cancel
    </Button>
  ];

  const isDisplayingMixedFormsErrorMessage = selectedGridIndexes?.length > 0 && isReAddingDisabled && isUpdatingAndRemovingDisabled;

  return (
    <>
      <DialogContainer
        id="remove-form-warning-dialog"
        visible={showRemoveFormDialog}
        title="Remove Form"
        modal
        width={500}
        focusOnMount={false}
        actions={removeFormDialogActions}
      >
        <TextBlock field="Are you sure you want to remove this form from the case?" />
      </DialogContainer>
      <MDialog
        id="manage-forms-dialog"
        className="manage-forms-dialog-container"
        title="Manage Forms"
        visible={manageForms}
        contentActions={getManageFormDialogActions()}
        focusOnMount={false}
        width={600}
      >
        {isReaddingCaseForm ? <MSpinner /> :
          <>
            <MGrid
              columnDefinitions={[{ key: "name", name: "Forms", dataSource: "name" }]}
              data={allCaseFormsList?.map(f => ({ name: f.title ? `${f.availForm.name} - ${f.title}${f.isDeleted ? " (Removed)" : ""}`: `${f.availForm.name}${f.isDeleted ? " (Removed)" : ""}`, value: f.availForm.id }))}
              isFetchingData={fetchingAllCaseForms}
              disableFilters
              allowRowSelection
              onRowSelectionChanged={indexes => setSelectedGridIndexes(indexes)}
              selectedRows={selectedGridIndexes}
            />
            <div className="manage-forms-dropdown-avail-div">
              <CardText>
                <p>Available Forms</p>
              </CardText>
              <MSelect
                className="manage-forms-dropdown-avail-mselect"
                options={availCaseFormsList?.filter(f => f.phoenixFormId !== null).map(f => ({ label: f.name, value: f.id })).sort((a, b) => a.label.localeCompare(b.label))}
                isClearable={true}
                onChange={e => {
                  if (e) {
                    let selected = availCaseFormsList?.find(f => f.id === e.value);
                    setSelectedCaseForm(selected);
                  }
                  else {
                    setSelectedCaseForm(null);
                  }
                }}
                getMenuPortalTarget={() => document.querySelector(".manage-forms-dialog-container")}
              />
            </div>
            <TextField
              id="manage-forms-title-field"
              className="manage-forms-title-field"
              rows={1}
              label="Add title for this form/supplement:"
              type="text"
              onChange={e => formTitle.current = e}
            />
            {isDisplayingMixedFormsErrorMessage && <p className="manage-forms-mixed-forms-error-message">Mix of removed and existing forms selected</p>}
            </>}
      </MDialog>
    </>
  );
};

const mapStateToProps = state => ({
  allCaseFormsList: state.caseForms.allCaseFormsList,
  availCaseFormsList: state.caseForms.availCaseFormsList,
  manageForms: state.caseForms.manageForms,
  fetchingAllCaseForms: state.caseForms.fetchingAllCaseForms,
});

const mapDispatchToProps = dispatch => ({
  getAllCaseForms(controlNumber) {
    dispatch(getAllCaseFormsRedux(controlNumber));
  },
  getAvailCaseFormsList(controlNumber) {
    dispatch(getAvailCaseFormsListRedux(controlNumber));
  },
  addCaseForm(controlNumber, payload, callback) {
    dispatch(addCaseFormRedux(controlNumber, payload, callback));
  },
  deleteCaseForm(controlNumber, payload, callback) {
    dispatch(deleteCaseForm(controlNumber, payload, callback));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  cancelManageForms(payload) {
    dispatch(manageFormsRedux(payload));
  },
  getCaseForms(controlNumber) {
    dispatch(getCaseForms(controlNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ManageFormsDialog);
